import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { StepState } from '@/components/ui/stepper'

export interface WizardStep<Id extends string = string> {
  id: Id
  title: string
  /** One line under the step's heading: what this step is for. */
  description: string
  optional?: boolean
}

/** Messages keyed by the id of the field they belong to, so the first can take focus. */
export type FieldErrors = Record<string, string>

const hasErrors = (errors: FieldErrors) => Object.keys(errors).length > 0

/**
 * Drives a multi-step form. The current step lives in `?step=`, so the
 * browser's Back button steps back and a refresh keeps the place. Moving
 * forward checks every step on the way; the first one still missing
 * something opens with its errors shown and its first invalid field focused.
 * `validate` is called on every render, so it must be cheap and pure.
 */
export function useFormWizard<Id extends string>(
  steps: readonly WizardStep<Id>[],
  validate: (id: Id) => FieldErrors,
) {
  const [params, setParams] = useSearchParams()
  // Steps whose errors are on screen: errors appear once someone tries to move on.
  const [shown, setShown] = useState<ReadonlySet<Id>>(() => new Set())
  const [serverErrors, setServerErrors] = useState<Partial<Record<Id, FieldErrors>>>({})
  const [reached, setReached] = useState(0)
  // What takes focus after the next render: the first of these fields on screen, or
  // the step's heading when there are none.
  const [focus, setFocus] = useState<{ targets: string[]; step: Id; count: number }>(() => ({
    targets: [],
    step: steps[0].id,
    count: 0,
  }))

  const firstInvalidBefore = (target: number) => {
    for (let index = 0; index < target; index++) {
      if (hasErrors(validate(steps[index].id))) return index
    }
    return target
  }

  // A step can't be opened past one that still needs something, even from a link.
  const requested = Math.max(
    0,
    steps.findIndex((step) => step.id === params.get('step')),
  )
  const index = firstInvalidBefore(requested)
  const step = steps[index]
  const stepParam = index === 0 ? null : step.id

  useEffect(() => {
    if (params.get('step') === stepParam) return
    const next = new URLSearchParams(params)
    if (stepParam) next.set('step', stepParam)
    else next.delete('step')
    setParams(next, { replace: true })
  }, [params, setParams, stepParam])

  useEffect(() => {
    setReached((current) => Math.max(current, index))
  }, [index])

  function errorsAt(position: number): FieldErrors {
    const id = steps[position].id
    return { ...(shown.has(id) ? validate(id) : {}), ...serverErrors[id] }
  }

  function open(position: number, focusTargets: string[] = []) {
    // A server's objection was seen on its step; once the person moves on, the next send rechecks it.
    if (position !== index && serverErrors[step.id]) {
      setServerErrors(({ [step.id]: _seen, ...rest }) => rest as Partial<Record<Id, FieldErrors>>)
    }
    const next = new URLSearchParams(params)
    if (position === 0) next.delete('step')
    else next.set('step', steps[position].id)
    if (position !== index) setParams(next)
    setFocus((current) => ({
      targets: focusTargets,
      step: steps[position].id,
      count: current.count + 1,
    }))
  }

  /** Shows a step's errors and takes the person there. */
  function stopAt(position: number, errors: FieldErrors) {
    setShown((current) => new Set(current).add(steps[position].id))
    open(position, Object.keys(errors))
  }

  /** Opens a step. Going back is free; going forward checks the steps in between. */
  function goTo(position: number) {
    if (position <= index) {
      if (position !== index) open(position)
      return true
    }
    for (let between = index; between < position; between++) {
      const errors = validate(steps[between].id)
      if (hasErrors(errors)) {
        stopAt(between, errors)
        return false
      }
    }
    open(position)
    return true
  }

  /** Checks every step; returns true when the whole form can be sent. */
  function validateAll() {
    setServerErrors({})
    for (let position = 0; position < steps.length; position++) {
      const errors = validate(steps[position].id)
      if (hasErrors(errors)) {
        stopAt(position, errors)
        return false
      }
    }
    return true
  }

  /** Puts errors the server returned on their step, and opens it. */
  function showServerErrors(id: Id, errors: FieldErrors) {
    setServerErrors((current) => ({ ...current, [id]: errors }))
    const position = steps.findIndex((candidate) => candidate.id === id)
    if (position >= 0) open(position, Object.keys(errors))
  }

  function stateOf(position: number): StepState {
    if (position === index) return 'current'
    if (hasErrors(errorsAt(position))) return 'invalid'
    if (position <= reached && !hasErrors(validate(steps[position].id))) return 'complete'
    return 'upcoming'
  }

  return {
    steps,
    step,
    index,
    isFirst: index === 0,
    isLast: index === steps.length - 1,
    nextStep: steps[index + 1] ?? null,
    /** Errors to show on the current step. */
    errors: errorsAt(index),
    focus,
    goTo,
    goToStep: (id: Id) => goTo(steps.findIndex((candidate) => candidate.id === id)),
    next: () => goTo(index + 1),
    back: () => open(Math.max(0, index - 1)),
    validateAll,
    showServerErrors,
    stateOf,
  }
}

export type FormWizardState<Id extends string = string> = ReturnType<typeof useFormWizard<Id>>
